/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

export class AppUtils {
    static FIRST_AIRED = 'First aired: ';

    static LONG_DASH = '\u2013';

    /**
     * Returns string for Day and Date in format: Day(Xxx) mm/dd
     * @param dateTime : Date
     * @returns string
     */
    static formatDayAndDate = (dateTime: Date, lang: string): string => {
        const _dateTime = new Date(dateTime);
        const formattedDate = _dateTime.toLocaleDateString(lang, {
            weekday: 'short',
        });
        const day = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
        const date = _dateTime.getDate();
        const month = _dateTime.getMonth() + 1;
        return `${day} ${month}/${date}`;
    };

    /**
     * Time is rounded down to 15 minutes
     * Returns a string time in ISO format without the milliseconds
     * example: 2022-09-05T19:00:00Z
     * @param currentTime : number, time in milliseconds
     * @returns string
     */
    static timeInISOFormatWithoutMilliSeconds(currentTime: number) {
        return new Date(currentTime).toISOString().split('.')[0].concat('Z');
    }

    /**
     * Returns string for time in format: h:mm
     * @param dateTime This fn will format time in 12 hr format.
     * @returns string (12hr format, eg. 12:03am, 8:00pm)
     */
    static formatTime = (dateTime) => {
        const time = new Date(dateTime);
        const minutes = String(time.getMinutes()).padStart(2, '0');
        const hour = time.getHours() % 12;
        const timePhase = Math.floor(time.getHours() / 12) ? 'pm' : 'am';
        return `${hour > 0 ? hour : '12'}:${minutes}${timePhase}`;
    };

    /**
     * This fn will format time in hr, min and hr min format.
     * @param millisecond time in milliseconds
     * @returns string (hr, min and hr min format, eg. 1 hr, 30 min, 1 h 30 min)
     */
    static formatTimeInHM = (millisecond: number) => {
        const minutesTotal = millisecond / (60 * 1000);
        const hours = Math.floor(minutesTotal / 60);
        const minutes = Math.floor(minutesTotal % 60);
        if (hours > 0) {
            if (minutes > 0) return `${hours} hr ${minutes} min`;
            return `${hours} hr`;
        }
        return `${minutes} min`;
    };

    /**
     * Returns an array of timeTexts (string) at specified span in given duration.
     * @param dateTime: startTime
     * @param span: intervals at which timeTexts has to be generated in minutes.
     * @param duration: total duration in hours.
     * @returns timeTexts: array:strings[]
     */
    static generateTimeTexts = (dateTime, span = 30, duration = 2) => {
        const timeTexts: string[] = [];
        for (let i = 0; i < (duration * 60) / span; i += 1) {
            timeTexts.push(AppUtils.formatTime(dateTime.getTime() + i * span * 60 * 1000));
        }
        return timeTexts;
    };

    /**
     * Round the given dateTime to nearest interval in minutes.
     * @param dateTime Date
     * @param interval minutes (default 30 min)
     * @param isPast boolean (default false). If `true`; rounded time will be from past else
     *  from future.
     * @returns Date
     */
    static roundTimeToNearestInterval = (
        dateTime: Date,
        interval: number = 30,
        isPast: boolean = false,
    ): Date => {
        const modMillis = dateTime.getTime() % (interval * 60 * 1000);
        return new Date(isPast ? dateTime.getTime() - modMillis : dateTime.getTime() + modMillis);
    };

    /**
     * Round the given time to nearest quarter time.
     * @param time Date
     * @param timeOffset in minutes.
     * @returns time in milliseconds(epoch timestamp).
     */
    static roundTimeQuarterHour = (time: Date, timeOffset: number): number => {
        const timeToReturn = new Date(time);
        timeToReturn.setMilliseconds(Math.round(timeToReturn.getMilliseconds() / 1000) * 1000);
        timeToReturn.setSeconds(Math.round(timeToReturn.getSeconds() / 60) * 60);
        timeToReturn.setMinutes(Math.round(timeToReturn.getMinutes() / timeOffset) * timeOffset);
        return timeToReturn.getTime();
    };

    /**
     * Round the given time to nearest quarter time up.
     * @param time in milliseconds(epoch timestamp).
     * @param timeOffset in minutes.
     * @returns time in milliseconds(epoch timestamp).
     */
    static roundTimeToNearestQuarterTimeUp = (time: number, timeOffset: number) => {
        const ttu = new Date(time + (timeOffset / 2) * 60 * 1000);
        return this.roundTimeQuarterHour(ttu, timeOffset);
    };

    /**
     * Round the given time to nearest quarter time down.
     * @param time in milliseconds(epoch timestamp).
     * @param timeOffset in minutes.
     * @returns time in milliseconds(epoch timestamp).
     */
    static roundTimeToNearestQuarterTimeDown = (time: number, timeOffset: number) => {
        const ttd = new Date(time - (timeOffset / 2) * 60 * 1000);
        return this.roundTimeQuarterHour(ttd, timeOffset);
    };

    /**
     * Return new date after adding the hours to the give dateTime
     * @param dateTime Date
     * @param hours number of hours
     * @returns Date
     */
    static addHoursToDateTime = (dateTime: Date, hours: number): Date => {
        const millis = hours * 60 * 60 * 1000;
        return new Date(dateTime.getTime() + millis);
    };

    /**
     * Return new date after subtracting the hours from the give dateTime
     * @param dateTime Date
     * @param hours number of hours
     * @returns Date
     */
    static subtractHoursFromDateTime = (dateTime: Date, hours: number): Date => {
        const millis = hours * 60 * 60 * 1000;
        return new Date(dateTime.getTime() - millis);
    };

    /**
     * Return new date after adding the minutes to the give dateTime
     * @param dateTime Date
     * @param minutes number of minutes
     * @returns Date
     */
    static addMinutesToDateTime = (dateTime: Date, minutes: number): Date => {
        const millis = minutes * 60 * 1000;
        return new Date(dateTime.getTime() + millis);
    };

    /**
     * Return new date after subtracting the minutes from the give dateTime
     * @param dateTime Date
     * @param minutes number of minutes
     * @returns Date
     */
    static subtractMinutesFromDateTime = (dateTime: Date, minutes: number): Date => {
        const millis = minutes * 60 * 1000;
        return new Date(dateTime.getTime() - millis);
    };

    /**
     * Return new date after adding the seconds to the give dateTime
     * @param dateTime Date
     * @param seconds number of seconds
     * @returns Date
     */
    static addSecondsToDateTime = (dateTime: Date, seconds: number): Date => {
        const _dateTime = new Date(dateTime);
        const millis = seconds * 1000;
        return new Date(_dateTime.getTime() + millis);
    };

    /**
     * Return new date after subtracting the seconds from the give dateTime
     * @param dateTime Date
     * @param seconds number of seconds
     * @returns Date
     */
    static subtractSecondsFromDateTime = (dateTime: Date, seconds: number): Date => {
        const _dateTime = new Date(dateTime);
        const millis = seconds * 60 * 1000;
        return new Date(_dateTime.getTime() - millis);
    };

    /**
     * This Returns a string id
     * @param horizontalPosition number
     * @param verticalPosition number
     * @returns string
     */
    static createHighlightId = (horizontalPosition: number, verticalPosition: number) => {
        return `${horizontalPosition}_${verticalPosition}`;
    };

    /**
     * This Returns time text (startTime - endTime)
     * @param startTime Date
     * @param endTime Date
     * @param duration number
     * @returns string
     */
    static generateDateTimeText = (
        startTime: Date,
        endTime: Date,
        duration: number,
        lang: string,
    ): string => {
        if (startTime && endTime) {
            return `${AppUtils.formatDayAndDate(startTime, lang)} ${AppUtils.formatTime(
                startTime,
            )} ${AppUtils.LONG_DASH} ${AppUtils.formatTime(endTime)}`;
        }

        if (startTime && duration) {
            const _endTime = AppUtils.addSecondsToDateTime(startTime, duration);
            return `${AppUtils.formatDayAndDate(startTime, lang)} ${AppUtils.formatTime(
                startTime,
            )} ${AppUtils.LONG_DASH} ${AppUtils.formatTime(_endTime)}`;
        }

        if (endTime && duration) {
            const _startTime = AppUtils.subtractSecondsFromDateTime(endTime, duration);
            return `${AppUtils.formatDayAndDate(_startTime, lang)} ${AppUtils.formatTime(
                _startTime,
            )} ${AppUtils.LONG_DASH} ${AppUtils.formatTime(endTime)}`;
        }

        return '';
    };

    /**
     * This Returns season and episode text
     * @param seasonNumber number
     * @param episodeNumber number
     * @returns string
     */
    static generateSeasonEpisodeText = (seasonNumber: number, episodeNumber: number): string => {
        return seasonNumber && episodeNumber ? `S${seasonNumber} E${episodeNumber}` : '';
    };

    /**
     * This Returns base 64 string
     * @param input input string
     * @returns base 64 string
     */
    static getBase64String = (input: string): string => {
        return Buffer.from(input).toString('base64');
    };

    /**
     * This Returns time text (startTime - endTime)
     * @param startTime Date
     * @param endTime Date
     * @param duration number
     * @returns string
     */
    static generateTimeText = (startTime: Date, endTime: Date, duration?: number): string => {
        if (startTime && endTime) {
            return `${this.formatTime(startTime)} - ${this.formatTime(endTime)}`;
        }

        if (startTime && duration) {
            const _endTime = this.addSecondsToDateTime(startTime, duration);
            return `${this.formatTime(startTime)} - ${this.formatTime(_endTime)}`;
        }

        const _startTime = this.subtractSecondsFromDateTime(endTime, duration!);
        return `${this.formatTime(_startTime)} - ${this.formatTime(endTime)}`;
    };

    static filterUnknownOrEmpty = (params: JSON) => {
        Object.keys(params)
            .filter((key) => params[key] === 'Unknown' || params[key] === '')
            // eslint-disable-next-line no-param-reassign
            .forEach((key) => delete params[key]);
    };

    static filterUnknownOrEmptyOrUndefined = (params: JSON) => {
        Object.keys(params)
            .filter(
                (key) =>
                    params[key] === 'Unknown' || params[key] === '' || params[key] === undefined,
            )
            // eslint-disable-next-line no-param-reassign
            .forEach((key) => delete params[key]);
    };

    /**
     * This function checks either value empty or not
     * @param value can be null, undefined, {}, [], ''
     * @returns boolean
     */
    static isEmpty = (value: any): boolean => {
        const valueType = typeof value;
        if (value !== null && valueType === 'object') {
            const objectProperties = Object.keys(value);
            return objectProperties.length === 0;
        }
        return !value;
    };

    /**
     * This Returns string of date with specified format
     * @param date Date or string or number
     * @param format string Format Specifier
     * @returns string
     */
    static strftime = (date: Date | string | number, format: string): string => {
        const _date = new Date(date);
        const formatOptions = {
            '%y': _date.getFullYear().toString().slice(-2),
            '%Y': _date.getFullYear(),
            '%m': (_date.getMonth() + 1).toString().padStart(2, '0'),
            '%M': (_date.getMonth() + 1).toString(),
            '%d': _date.getDate().toString().padStart(2, '0'),
            '%D': _date.getDate().toString(),
            '%h': _date.getHours().toString().padStart(2, '0'),
            '%H': _date.getHours().toString(),
            '%i': _date.getMinutes().toString().padStart(2, '0'),
            '%I': _date.getMinutes().toString(),
            '%s': _date.getSeconds().toString().padStart(2, '0'),
            '%S': _date.getSeconds().toString(),
        };

        const formattedDate = format.replace(/%[yYmMdDhHiIsS]/g, (match) => {
            return formatOptions[match] || match;
        });

        return formattedDate;
    };

    /**
     * Recursively merges objects from an array into a single object.
     * @param {Object[]} arrayOfObjects - An array containing objects to merge.
     * @returns {Object} - A single merged object.
     */
    static mergeObjects = <T extends object>(arrayOfObjects: Array<T>): T => {
        return arrayOfObjects.reduce((accumulator, currentObject) => {
            Object.keys(currentObject).forEach((key) => {
                if (
                    typeof currentObject[key] === 'object' &&
                    currentObject[key] !== null &&
                    !Array.isArray(currentObject[key])
                ) {
                    accumulator[key] = AppUtils.mergeObjects([
                        accumulator[key] || {},
                        currentObject[key],
                    ]);
                } else {
                    accumulator[key] = currentObject[key];
                }
            });
            return accumulator;
        }, {} as T);
    };
}
